import NotFoundError from "~/libs/errors/NotFoundError";
import type {BaseApiFetchOptions} from "~/libs/interfaces/BaseApiFetchOptions";
import type GameDrawLocal from "~/libs/interfaces/GameDrawLocal";
import type GameDrawFilters from "~/libs/interfaces/GameDrawFilters";
import type GameDrawStrapi from "~/libs/interfaces/GameDrawStrapi";
import gameDrawLocalFactory from "~/libs/factories/GameDrawLocalFactory";
import type UserPickupWinPayload from "~/libs/interfaces/UserPickupWinPayload";

const gameDrawStore = (type: string = 'game', key?: string) => defineStore(`draws-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      offset: Ref<number>
      draws: GameDrawLocal[]
      draw: Ref<GameDrawLocal | null>
      activeDraws: Ref<Record<string, GameDrawLocal>>
      filters: GameDrawFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      offset: ref(0),
      draws: reactive([]),
      draw: ref(null),
      activeDraws: ref({}),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    },
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    setOffset(offset: number) {
      this.offset = offset
    },
    /**
     * Get draws with applied filters and presets
     * @param offset
     * @param limit
     * @return GameDrawLocal[]
     */
    getFilteredDraws(offset: number = 0, limit: number = 10): GameDrawLocal[] {
      let matchedCount = 0

      const draws = this.draws.map((draw) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...draw,
          isEnabled: matchFilters
        }
      })
      return draws
    },

    /**
     * Get only filtered draws (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredDraws(offset: number = 0, limit: number = 10): GameDrawLocal[] {
      const draws = this.getFilteredDraws(offset, limit)
      return draws//.filter((draw) => draw.isEnabled)
    },

    /**
     * Get count of getFilteredDraws
     * @return number
     */
    getFilteredDrawsCount(): number {
      const draws = this.getFilteredDraws(0, this.draws.length).filter(game => true /*draw.isEnabled*/)
      return draws?.length || 0
    },

    /**
     * Get count of draws
     * @return number
     */
    getDrawsCount(): number {
      return this.draws.length || 0
    },
    /**
     * Get draw by drawId
     * @param id
     */
    getDraw(id: string|number): GameDrawLocal | undefined {
      const draw = this.draws.find((draw) => {
        return String(draw.id) === String(id)
      })
      return draw
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: GameDrawFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      // check item game ids filter
      if (!sameArrays(filters?.gameIds, this.filters.gameIds)) {
        return true
      }

      if (filters?.isActive !== this.filters.isActive) {
        return true
      }

      return false
    },

    checkFiltersChanges(filters: GameDrawFilters): void {
      // reset items if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.filters.gameIds = filters.gameIds
        this.filters.isActive = filters.isActive
        this.draws = reactive([])
        this.draw = null
      }
    },

    async fetchActiveGameDraw(gameDocumentId?: string): Promise<GameDrawLocal | null> {
      if (!gameDocumentId) {
        return null
      }

      const logger = useFrontendLogger()
      const { find } = useStrapi()

      const params = {
        filters: {
          game: gameDocumentId
        },
      }

      try {
        const result = await find<any>('game-draws/active-draw', params)
        if (result) {
          const gameDraw = gameDrawLocalFactory.createGameDrawFromStrapi(result as unknown as GameDrawStrapi)
          if (gameDraw) {
            this.activeDraws[gameDocumentId] = gameDraw
          }
        }
        return this.activeDraws[gameDocumentId]
      } catch (e) {
        logger?.error('useGameDrawsStore:getActiveGameDraw - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },

    async checkWin(gameDocumentId?: string): Promise<GameDrawLocal | null> {
      if (!gameDocumentId) {
        return null
      }

      const userStore = useUserStore()
      if (!userStore.isLoggedIn) {
        return null
      }

      const logger = useFrontendLogger()
      const { create } = useStrapi()

      const payload = {
        game: gameDocumentId
      }

      try {
        const result = await create<any>('games/check-win', payload)
        const gameDraw = gameDrawLocalFactory.createGameDrawFromStrapi(result as unknown as GameDrawStrapi)
        if (gameDraw) {
          this.activeDraws[gameDocumentId] = gameDraw
        }
        return this.activeDraws[gameDocumentId]
      } catch (e) {
        logger?.error('useGameDrawsStore:checkWin - exception', {
          errorDetail: e,
          strapiCallParams: payload,
        })
        this.isLoading = false
        throw e
      }
    },

    async pickupWin(payload: UserPickupWinPayload): Promise<GameDrawLocal | null> {
      if (!payload.game) {
        return null
      }

      const userStore = useUserStore()
      if (!userStore.isLoggedIn) {
        return null
      }

      const logger = useFrontendLogger()
      const { create } = useStrapi()

      const requestData = {
        game: payload.game,
        name: payload.name,
        street: payload.street,
        city: payload.city,
        psc: payload.psc,
        phone: payload.phone,
        bankAccountNumber: payload.bankAccountNumber,
      }

      try {
        const result = await create<any>('games/pickup-win', requestData)
        const gameDraw = gameDrawLocalFactory.createGameDrawFromStrapi(result as unknown as GameDrawStrapi)
        if (gameDraw) {
          this.activeDraws[payload.game] = gameDraw
        }
        return this.activeDraws[payload.game]
      } catch (e) {
        logger?.error('useGameDrawsStore:pickupWin - exception', {
          errorDetail: e,
          strapiCallParams: payload,
        })
        this.isLoading = false
        throw e
      }
    },

    getActiveDraw(gameDocumentId?: string): GameDrawLocal | null {
      if (!gameDocumentId) {
        return null
      }
      return this.activeDraws[gameDocumentId]
    },

    /**
     * Fetch draws from server
     * @return {Promise<void>}
     */
    async fetchGameDraws(
      filters: GameDrawFilters = {},
      options: BaseApiFetchOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const { find } = useStrapi()

      this.isLoading = true

      const apiFilters = {
        ...(filters.ids && {
          documentId: {
            $in: filters.ids,
          }
        }),
        ...(filters.gameIds && {
          game: {
            documentId: {
              $in: filters.ids,
            }
          }
        }),
        ...(filters.closeDatetimeFrom && {
          closeDatetime: {
            $gte: filters.closeDatetimeFrom,
          }
        }),
        ...(filters.closeDatetimeTo && {
          closeDatetime: {
            $lte: filters.closeDatetimeTo,
          }
        }),
        ...(filters.onlyFinished && {
          $or: [
            {
              game_draw_winner: {
                $notNull: true,
              },
            },
            {
              closeDatetime: {
                $lte: new Date(),
              },
            },
          ],
        }),
        ...(typeof filters.gameIds !== 'undefined' && {
          isActive: {
            $eq: filters.isActive,
          }
        }),
      }

      const params = {
        filters: apiFilters,
        pagination: {
          start: this.offset,
          limit: this.limit,
        },
        populate: {
          game: true
        },
        sort: ["drawDatetime:desc"]
      }

      try {
        const result = await find<GameDrawStrapi>('game-draws', params)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useGameDrawsStore:fetchGameDraws - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },
    /**
     * Process loaded data from Strapi
     * @param newItems
     * @param isDetail
     */
    processData(newItems: GameDrawStrapi[], isDetail: boolean = false):void {
      if (newItems.length) {
        // check item detail load
        if (isDetail && newItems[0]) {
          this.setItem(gameDrawLocalFactory.createGameDrawFromStrapi(newItems[0]))
        } else if (isDetail && (!newItems || !newItems.length)) {
          throw new NotFoundError('Game not found')
        }
        if (newItems && newItems.length) {
          // add new items to list
          this.addItems(newItems.map(gameDrawLocalFactory.createGameDrawFromStrapi).filter(item => !!item), true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Game not found')
      }
    },
    /**
     * Add draws to store - remove duplicities
     * @param newItems
     * @param rewrite
     */
    addItems(newItems: GameDrawLocal[], rewrite: boolean = false): void {
      newItems.forEach((newItem) => {
        const foundIndex = this.draws.findIndex(draw => draw.id === newItem.id)
        if (foundIndex >= 0 && rewrite) {
          this.draws.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.draws.push(newItem)
        }
      })

      // sort articles
      this.draws.sort((a, b) => {
        if (a.drawDatetime && b.drawDatetime) {
          return a.drawDatetime?.getTime() > b?.drawDatetime?.getTime() ? -1 : 1
        }
        return 1
      })
    },

    setItem(item?: GameDrawLocal): void {
      if (item) {
        this.draw = item
      } else {
        this.draw = null
      }
    }

  }
})

export const useGameDrawsStore = (type?: string, key?: string) => {
  return gameDrawStore(type, key)()
}
